import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails,
  Grid, Paper, CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { applyFieldEnhancement, translateEnhancedOption } from '../../constants/listingFieldEnhancements';

/**
 * ListingDetailsView
 *
 * Affiche en lecture seule les détails structurés d'une annonce (JSONB `details`).
 * Résout les labels et options via le namespace i18next "listingFields" servi depuis la DB.
 *
 * Convention de clés :
 *   - label  → t('listingFields:field_key', def.label)
 *   - option → t('listingFields:opt_CanonicalValue', opt)
 *
 * Props:
 *   - listing       : objet annonce complet (ou au moins { details, offer_type })
 *   - expanded      : bool (géré par le parent)
 *   - onChange      : (event, isExpanded) => void — pour le contrôle de l'accordéon
 *   - accordionSx   : sx optionnel pour surcharger le style de l'accordéon
 */
const ListingDetailsView = ({ listing, expanded, onChange, accordionSx = {} }) => {
  const { t, i18n } = useTranslation(['listing', 'common', 'listingFields']);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [loading, setLoading] = useState(false);

  const details = (() => {
    const raw = listing?.details;
    if (!raw) return undefined;
    // Si c'est une chaîne JSON, parser
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch (e) {
        console.error('[ListingDetailsView] Failed to parse details JSON:', e);
        return raw;
      }
    }
    // Sinon, retourner comme-est (objet)
    return raw;
  })();
  const offerType = listing?.offer_type;

  const hasDetails = details && typeof details === 'object' && Object.keys(details).length > 0;

  const fetchFieldDefs = useCallback(async (type) => {
    if (!type) return;
    setLoading(true);
    try {
      const res = await api.get('/listing-field-definitions', { params: { offer_type: type } });
      if (res.data?.success) {
        setFieldDefs((res.data.data || []).map(applyFieldEnhancement));
      }
    } catch (err) {
      console.error('[ListingDetailsView] Erreur fetch definitions:', err);
      setFieldDefs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('[ListingDetailsView] mounted/changed - details:', details, 'offerType:', offerType);
    fetchFieldDefs(offerType);
  }, [offerType, fetchFieldDefs]);

  // Ne rien afficher s'il n'y a pas de détails
  if (!hasDetails) {
    console.log('[ListingDetailsView] No details to display - hasDetails:', hasDetails);
    return null;
  }

  const detailKeys = Object.keys(details);

  /** Formate une valeur selon le type de champ */
  const formatValue = (def, raw) => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (!def) return String(raw);

    if (def.field_type === 'boolean') {
      const isTrue = raw === true || raw === 'true';
      return (
        <Chip
          icon={isTrue ? <CheckIcon /> : <CloseIcon />}
          label={isTrue ? t('common:common.yes', 'Oui') : t('common:common.no', 'Non')}
          size="small"
          color={isTrue ? 'success' : 'default'}
          variant="outlined"
        />
      );
    }

    if (def.field_type === 'select') {
      const canonicalValue = String(raw);
      let label;
      if (def._optionI18n) {
        const local = translateEnhancedOption(canonicalValue, def, i18n.language);
        label = local !== null ? local : t(`listingFields:opt_${canonicalValue}`, canonicalValue);
      } else {
        label = t(`listingFields:opt_${canonicalValue}`, canonicalValue);
      }
      const colorEntry = def._optionI18n?.find((o) => o.value === canonicalValue);
      if (colorEntry?.colorHex) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: colorEntry.colorHex, border: '1px solid rgba(0,0,0,0.2)', flexShrink: 0 }} />
            <Chip label={label} size="small" color="primary" variant="outlined" />
          </Box>
        );
      }
      return <Chip label={label} size="small" color="primary" variant="outlined" />;
    }

    if (def.field_type === 'number') {
      const num = Number(raw);
      if (isNaN(num)) return String(raw);
      return (
        <Typography component="span" variant="body2" fontWeight={600}>
          {num}{def.unit ? ` ${def.unit}` : ''}
        </Typography>
      );
    }

    return (
      <Typography component="span" variant="body2">
        {String(raw)}{def.unit ? ` ${def.unit}` : ''}
      </Typography>
    );
  };

  const rows = detailKeys.map(key => {
    const def = fieldDefs.find(d => d.key === key);
    const translatedLabel = def
      ? t(`listingFields:${key}`, def.label)
      : key.replace(/_/g, ' ');
    const label = def?.icon ? `${def.icon} ${translatedLabel}` : translatedLabel;
    const raw = details[key];
    const formatted = formatValue(def, raw);
    if (formatted === null) return null;
    return { key, label, formatted };
  }).filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={{
        mb: 3,
        boxShadow: 2,
        '&:before': { display: 'none' },
        borderRadius: '8px !important',
        overflow: 'hidden',
        ...accordionSx
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          '& .MuiAccordionSummary-content': { alignItems: 'center' },
          '& .MuiAccordionSummary-expandIconWrapper': { color: 'white' },
          '&:hover': {
            background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
          }
        }}
      >
        <InfoOutlinedIcon sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
          🏷️ {t('listing:details.title', 'Détails spécifiques')}
        </Typography>
        {!loading && (
          <Chip
            label={rows.length}
            size="small"
            sx={{ ml: 1.5, bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700, fontSize: '0.75rem' }}
          />
        )}
      </AccordionSummary>

      <AccordionDetails sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {rows.map(({ key, label, formatted }) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    height: '100%'
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
                  >
                    {label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {formatted}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ListingDetailsView;
